import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDateShort, fmtINR } from "@/lib/api";
import { ArrowRight, MapPin, Calendar, Sparkles, Users, HandCoins, ScanLine } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";

export default function Home() {
  const [events, setEvents] = useState([]);
  const [impact, setImpact] = useState(null);
  const loadData = () => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/impact").then((r) => setImpact(r.data)).catch(() => {});
  };

  useEffect(() => {
    loadData();
    const handleReady = () => loadData();
    window.addEventListener("cgc:server-ready", handleReady);
    return () => window.removeEventListener("cgc:server-ready", handleReady);
  }, []);
  const featured = events.filter((e) => e.featured);
  const rest = events.slice(0, 6);
  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 cgc-noise pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-6">Visakhapatnam Chapter · 2026 Season</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[0.95]">
              A calendar of things
              <br />
              <span className="text-[color:var(--brand-terracotta)]">worth showing up for.</span>{" "}
            </h1>
            <p className="mt-6 text-lg text-stone-600 dark:text-stone-400 max-w-xl">
              Fundraisers, weekend stalls, and skill workshops from Common Ground Collective — a Visakhapatnam-based social enterprise. Book a ticket, get a QR, meet the neighbours.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/events" data-testid="hero-browse-btn" className="btn-brand inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-medium">
                Browse events <ArrowRight size={16} />
              </Link>
              <a href="https://cgcollective.in" target="_blank" rel="noreferrer" data-testid="hero-main-site-link" className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-medium border border-border hover:bg-secondary">
                About Common Ground ↗
              </a>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-stone-500">
              <div className="flex items-center gap-2"><MapPin size={14} /> Vizag first</div>
              <div className="flex items-center gap-2"><Calendar size={14} /> Weekly programs</div>
              <div className="flex items-center gap-2"><Sparkles size={14} /> Free & paid</div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1759317714076-a3d664d39f8f?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Vizag coastline" className="rounded-2xl w-full h-[420px] object-cover" />
              <div className="absolute -bottom-4 -left-4 bg-background border border-border rounded-xl p-4 shadow-md max-w-[220px]">
                <div className="eyebrow">Manifesto</div>
                <p className="text-sm mt-2">Community first. Coastline first. No filler, no fluff.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact strip */}
      {impact && (
        <section className="border-y border-border bg-[color:var(--sand)] dark:bg-stone-900/40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
            <ImpactStat icon={HandCoins} value={fmtINR(impact.funds_raised_paise)} label={impact.funds_raised_label || "Raised on this platform"} tid="impact-funds" />
            <ImpactStat icon={Calendar} value={impact.events_total !== undefined ? impact.events_total : (impact.events_hosted + impact.events_upcoming)} label="Programs on the calendar" tid="impact-events" />
            <ImpactStat icon={Users} value={impact.unique_supporters} label={impact.supporters_label || "Supporters registered"} tid="impact-supporters" />
            <ImpactStat icon={MapPin} value={impact.chapters_count !== undefined ? impact.chapters_count : 1} label={impact.chapters_label || "Current chapters"} tid="impact-chapters" />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6 text-[11px] font-mono uppercase tracking-widest text-stone-500">
            {impact.tagline || "Live numbers from actual bookings — never inflated"}
          </div>
        </section>
      )}

      {/* Featured */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="eyebrow">Featured</div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold mt-2">This season&apos;s headline programs</h2>
            </div>
            <Link to="/events" className="text-sm underline underline-offset-4 hover:text-[color:var(--brand-terracotta)]" data-testid="featured-view-all">View all</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {featured.map((e) => (
              <EventCard key={e.id} e={e} big />
            ))}
          </div>
        </section>
      )}

      {/* Upcoming */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="mb-8">
          <div className="eyebrow">Upcoming</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mt-2">What&apos;s next on the ground</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((e) => <EventCard key={e.id} e={e} />)}
        </div>
      </section>
    </PublicLayout>
  );
}

function ImpactStat({ icon: Icon, value, label, tid }) {
  return (
    <div data-testid={tid} className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-md bg-[color:var(--brand-terracotta)]/10 text-[color:var(--brand-terracotta)] flex items-center justify-center flex-shrink-0">
        <Icon size={18} />
      </div>
      <div className="min-w-0">
        <div className="font-display text-2xl sm:text-3xl font-extrabold leading-none">{value}</div>
        <div className="text-xs text-stone-500 mt-1.5">{label}</div>
      </div>
    </div>
  );
}

function EventCard({ e, big }) {
  const minPrice = Math.min(...(e.tiers || []).map((t) => t.price_paise));
  const link = e.organizer_slug && e.organizer_slug !== "cgc" ? `/events/${e.organizer_slug}/${e.slug}` : `/events/${e.slug}`;
  const isCollab = e.organizer_slug && e.organizer_slug !== "cgc";
  return (
    <Link
      to={link}
      data-testid={`event-card-${e.slug}`}
      className="group cgc-card overflow-hidden flex flex-col hover:-translate-y-0.5 transition-transform"
    >
      <div className={`relative w-full ${big ? "h-64" : "h-48"} overflow-hidden`}>
        <img src={e.cover_image} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5 items-center">
          <span className="bg-background/90 backdrop-blur px-2.5 py-1 rounded-md text-[11px] font-mono uppercase tracking-wider">{e.category}</span>
          {isCollab && (
            <span className="bg-amber-600/90 text-white font-medium px-2 py-0.5 rounded text-[10px] uppercase tracking-wider shadow-sm">
              Collab · {e.organizer || e.organizer_slug}
            </span>
          )}
        </div>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="text-xs font-mono uppercase tracking-widest text-stone-500 mb-2 flex items-center gap-2">
          <Calendar size={12} /> {fmtDateShort(e.start_at)} · <MapPin size={12} /> {e.city}
        </div>
        <h3 className="font-display text-xl font-semibold leading-snug group-hover:text-[color:var(--brand-terracotta)]">{e.title}</h3>
        {e.subtitle && <p className="text-sm text-stone-600 dark:text-stone-400 mt-2 line-clamp-2">{e.subtitle}</p>}
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="font-mono">{minPrice === 0 ? "Free" : `From ${fmtINR(minPrice)}`}</span>
          <span className="text-[color:var(--brand-terracotta)] font-medium inline-flex items-center gap-1">View <ArrowRight size={14} /></span>
        </div>
      </div>
    </Link>
  );
}
